import { ElementRef, EventEmitter, Input, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { version } from '../../package.json';
import { util } from '../classes/util';
import { environment } from '../environment';
import { IMCMapView } from '../types';

import { ESRI } from './../esri-loader-util';
import { ScopeComponent } from './scope/scope.component';

const GoogleAnalyticsUrl = 'https://www.google-analytics.com/analytics.js';
const MapCoreGoogleAnalyticsTrackingId = 'UA-65152108-6';

let componentIdCounter = 1;

/** Base of MapCore components */
export class BaseComponent {
    constructor(host: ViewContainerRef, startReady?: boolean) {
        this.componentId = componentIdCounter++;
        if (startReady) {
            this.isReady();
        }
        if (host && host.element && host.element.nativeElement) {
            this.hostElement = host.element.nativeElement;
        }
        if (this.includeEsriCss) {
            this.setupEsriStylesheet();
        }
        if (this.includeOverrideCss) {
            this.setupStylesheetOverride();
        }
        if (this.includeGoogleAnalytics) {
            this.setupGoogleAnalytics();
        }

        this.unregisterWithMcScope = this.unregisterWithMcScope.bind(this);
    }
    public hostElement: HTMLDivElement;
    public componentId: number;

    /** Promise to notify when a component is ready */
    @Input() ready: Promise<boolean> = new Promise((resolve, reject) => {
        this._resolveReady = resolve;
        this._rejectReady = reject;
    });

    private _resolveReady: (value?: boolean | PromiseLike<boolean>) => void;
    private _rejectReady: (reason?: any) => void;

    private readonly registerAttributeEventSubscriptions = new Map<EventEmitter<any>, Subscription>();

    protected get includeEsriCss() { return true; }
    protected get includeOverrideCss() { return true; }
    protected get includeGoogleAnalytics() { return true; }
    protected isReady(also?: PromiseLike<boolean>) { this._resolveReady(also || true); }
    protected readyProblem(reason?: any) { this._rejectReady(reason); }

    private async getParentScope() {
        if (this.parentScope === undefined) {
            await this.waitForHostElement();
            const timeoutMilliseconds = 50;

            const parentScope = await util.retryUntilValue(() => {
                return util.getClosest(this.hostElement, 'mc-scope');
            }, timeoutMilliseconds);

            if (parentScope) {
                this.parentScope = parentScope;
            } else {
                this.parentScope = null;
            }
        }
        return this.parentScope;
    }
    private parentScope: ScopeComponent;

    /**
     * Returns a reference to the angular component (since angular doesn't seem to provide a way to do this?).
     * This is used for things like getClosestAncestor so it will always return the component instead of the html element.
     */
    @Input()
    public getComponentReference<T>() {
        return this as any as T;
    }

    /**
     * Allows child components to register themselves with mc-scope so they are discoverable by other components.
     * This is mainly called by maps, but mc-scope is used to resolve maps for many other components
     */
    protected async registerWithMcScope() {
        const mcScope = await this.getParentScope();
        if (mcScope) {
            mcScope.registerComponent(this);
        }
    }

    protected async unregisterWithMcScope() {
        const mcScope = await this.getParentScope();
        if (mcScope) {
            mcScope.unregisterComponent(this);
        }
    }

    /**
     * Given a tag, look for an attribute with `id` at the end and return its value, or null if not found.
     * (e.g `mc-map` would result in looking for `mc-map-id` and `mcMapId` )
     */
    public getAttributeValue(snakeCaseAttributeName: string) {
        if (this.hostElement.attributes) {
            const attributeNames = [snakeCaseAttributeName, util.snakeToCamelCase(snakeCaseAttributeName)];
            for (const attributeName of attributeNames) {
                const targetAttribute = this.hostElement.attributes.getNamedItem(attributeName);
                if (targetAttribute) {
                    const attributeValue = targetAttribute.value;
                    return attributeValue;
                }
            }
        }
        return;
    }

    /**
     * Certain lifecycle calls are run before the host element is ready, so await this promise
     * to be notified when the hostElement is ready
     */
    public async waitForHostElement() {
        const sleepMs = 15;
        const maxIterations = 20;
        for (let i = 0; i < maxIterations; i++) {
            if (this.hostElement) {
                return;
            }
            await util.sleep(sleepMs);
        }
    }

    /**
     * Get the closest parent with the specified tag. If a parent can't be found, ask the mc-scope
     * @param selector - the tag name of the of the ancestor component you want (i.e. "mc-map", "mc-legend", etc...)
     * @param id - the id of the ancestor component you want. This is optional, and is only used when asking mc-scope
     */
    public async getClosestAncestor<TagNameType extends keyof ComponentTagMap>(tagName: TagNameType): Promise<ComponentTagMap[TagNameType]> {
        const id = this.getAttributeValue(`${tagName}-id`);
        try {
            await this.waitForHostElement();
            // wait for host element to be initialized
            if (!this.hostElement) {
                return null;
            }
            // if the element does NOT have an ID attribute, try to find whether we are nested inside of the desired component
            if (!id) {
                // use the closest parent if possible
                const element = util.getClosest(this.hostElement, tagName);
                // if we found the parent, use it.
                if (element) {
                    await this.waitForShadowRoot(element);
                    return (element as BaseComponent).getComponentReference<ComponentTagMap[TagNameType]>();
                }
            }
            const scope = await this.getScope();
            if (scope) {
                // if scope has the element we are looking for, return it
                const component = await scope.getComponent(tagName, id);
                if (component) {
                    return component;
                } else {
                    throw new Error('Could not find the desired element in ancestor <mc-scope>');
                }
            } else {
                throw new Error('Could not find ancestor <mc-scope>');
            }
        } catch (e) {
            const targetIdText = id ? ` with id "${id}"` : '';
            const hostIdText = this.hostElement.id ? ` with id "${this.hostElement.id}"` : '';
            e = `Error loading closest ancestor <${tagName}${targetIdText}> for <${this.hostElement.tagName}>${hostIdText}: ${e}`;
            throw e;
        }
    }

    /**
     * Get the assigned component in Slot
     * @param selector - the tag name of the of the ancestor component you want (i.e. "mc-map", "mc-legend", etc...)
     * @param slotRef - the Slot Element reference
     */
    getSlotAssignedComponent<TagNameType extends keyof ComponentTagMap>(slotRef: ElementRef<HTMLSlotElement>, tagName?: TagNameType): ComponentTagMap[TagNameType] | any {
        if (slotRef && slotRef.nativeElement.assignedNodes().length > 0) {
            const element = slotRef.nativeElement.assignedNodes()[0];
            if (tagName && element.nodeName.toLowerCase() === tagName) {
                return element as any as ComponentTagMap[TagNameType];
            } {
                return element;
            }
        } else {
            throw new Error('No component assigned to the slot');
        }
    }

    /**
     * Get the parent mc-scope element. Will retry for up to half a second before
     * giving up and throwing an exception
     */
    private async getScope() {
        const maxDurationMilliseconds = 400;
        return util.retryUntilValue(async () => {
            // we did not find the parent. Search for a parent mc-scope
            const scope = util.getClosest(this.hostElement, 'mc-scope');
            // if we found mcScope
            if (scope) {
                // mc-scope definitely has shadow-root
                await this.waitForShadowRoot(scope);
                return scope;
            }
        }, maxDurationMilliseconds);
    }

    private async waitForShadowRoot<K extends keyof HTMLElementTagNameMap>(element: HTMLElementTagNameMap[K] | null) {
        const sleepMS = 50;
        const maxChecks = 20;
        let currentChecks = 0;
        while (!element.shadowRoot && currentChecks < maxChecks) {
            currentChecks++;
            await util.sleep(sleepMS);
        }
    }

    private async setupStylesheetOverride() {
        if (this.hostElement && window.overrideStylesheet) {
            const stylesheetLink = document.createElement('link');
            stylesheetLink.rel = 'stylesheet';
            stylesheetLink.href = window.overrideStylesheet;
            await this.addStyleElement(stylesheetLink, true);
        }
    }

    private async setupEsriStylesheet() {
        if (this.hostElement) {
            const stylesheet = document.createElement('style');
            stylesheet.innerText = `@import url('${ESRI.esriStylesUrl}');`;
            await this.addStyleElement(stylesheet);
        }
    }

    private async addStyleElement(el: HTMLLinkElement | HTMLStyleElement, last = false) {
        await this.waitForShadowRoot(this.hostElement);
        const shadowRoot = this.hostElement.shadowRoot;
        if (shadowRoot) {
            if (shadowRoot.children.length) {
                const styleBlocks = shadowRoot.querySelectorAll('style');
                if (styleBlocks.length && last) {
                    styleBlocks[styleBlocks.length - 1].after(el);
                } else {
                    shadowRoot.insertBefore(el, shadowRoot.firstChild);
                }
            } else {
                shadowRoot.append(el);
            }
        }
    }

    async setupMapViewRef(mapViewRef) {
        if (!mapViewRef) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                return (<any>parent).mapView as NonNullable<IMCMapView>;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        } else {
            return mapViewRef;
        }
    }

    private trackerName(trackingIdIndex: number) { return `${environment.appName}(MapCore_${version})_${trackingIdIndex}`; }

    private get trackingIds() {
        return environment.googleAnalyticsTrackingIds && environment.googleAnalyticsTrackingIds.length
            ? [MapCoreGoogleAnalyticsTrackingId, ...environment.googleAnalyticsTrackingIds]
            : [MapCoreGoogleAnalyticsTrackingId];
    }

    private runPerTrackingId(toRun: (trackingId: string, trackingIdIndex: number) => void) {
        for (let trackingIdIndex = 0; trackingIdIndex < this.trackingIds.length; trackingIdIndex++) {
            toRun(this.trackingIds[trackingIdIndex], trackingIdIndex);
        }
    }

    private async setupGoogleAnalytics() {
        if (environment.production) {
            window.GoogleAnalyticsObject = 'mc2ga';
            await util.loadScript(GoogleAnalyticsUrl, 'MC-GoogleAnalytics', () => window.mc2ga);
            window.GoogleAnalyticsObject = undefined;
            this.runPerTrackingId((trackingId, trackingIdIndex) => {
                let tracker = window.mc2ga.getByName(this.trackerName(trackingIdIndex));
                if (!tracker) {
                    tracker = window.mc2ga.create(trackingId, 'auto', this.trackerName(trackingIdIndex));
                    tracker.send('pageview');
                }
                tracker.send('event', {
                    eventCategory: this.hostElement.tagName,
                    eventAction: 'Component Loaded',
                    nonInteraction: true
                });
            });
        }
    }

    /**
     * Sends infomation to Google Analytics. Should only be used if extra data is needed, as a component loading is already sent.
     * @param eventAction - The event that occured.
     * @param nonInteraction - Should only be set to false if the event is triggered by user interaction.
     * @param eventCategory - The category of the event. Only set this if you want the event to show up some place specific. Defaults to the component's tag.
     */
    protected async sendAnalyticsEvent(eventAction: string, nonInteraction: boolean = true, eventCategory: string = null) {
        if (!window.mc2ga) {
            await this.setupGoogleAnalytics();
        }
        if (window.mc2ga) {
            if (!eventCategory) {
                eventCategory = this.hostElement.tagName;
            }
            this.runPerTrackingId((_trackingId, trackingIdIndex) => {
                const tracker = window.mc2ga.getByName(this.trackerName(trackingIdIndex));
                if (tracker) {
                    tracker.send('event', {
                        eventCategory,
                        eventAction,
                        nonInteraction
                    });
                }
            });
        }
    }

    /**
     * Take a string (like "a.b.c"), execute that function against window.
     * For example, window.a.b.c(value)
     */
    protected callOnWindow(path: string, ...args) {
        const parts = path.split('.');
        let obj: Function;
        obj = window as any;
        for (const part of parts) {
            obj = obj[part];
        }
        obj.call(window, ...args);
    }

    protected registerEvent(attributeName, emitter: EventEmitter<any>) {
        // clean up old subscriptions if the user changes this value
        if (this.registerAttributeEventSubscriptions.has(emitter)) {
            try {
                this.registerAttributeEventSubscriptions.get(emitter).unsubscribe();
            } catch (e) { }
        }
        // force attribute name to lower case for consistency
        attributeName = attributeName.toLowerCase();

        // subscribe to this event emitter
        const subscription = emitter.subscribe(evt => {
            // if there is an attribute with the specified name, call whatever the function it references
            const targetAttribute = this.hostElement.attributes.getNamedItem(attributeName);
            if (targetAttribute) {
                const attributeValue = targetAttribute.value;
                this.callOnWindow(attributeValue, evt);
            }
            if (this.hostElement[attributeName]) {
                this.hostElement[attributeName](evt);
            }
        });
        this.registerAttributeEventSubscriptions.set(emitter, subscription);
    }
}
